import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { EnergyMeasurementParameter } from 'lavva.exalushome/build/js/Services/Devices/IDeviceState';
import { EnergyHistory } from 'lavva.exalushome/build/js/Services/StatesHistory/StatesHistory';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ControlWrapper, InputSelect, TimeRanges } from '../../../../../../components';
import { Phases } from '../../../../../../components/channel-details-controls/meter/phases';
import { RangeHeader } from '../../../../../../components/range-header';
import { useInstallationContext } from '../../../../../../context/installation';
import { useChartData } from '../../../../hooks/charts';
import { energyParams } from '../../../../modules/action-create/utils/energy-parameters';
import { useMeterPhases } from '../../controls/meter/hooks/use-meter-phases';
import { ChartEmptyContainer } from '../components/chart-empty-container/chart-empty-container';
import ChartSummary from '../components/chart-summary';
import CustomizedXTick from '../components/customized-x-tick';
import { ExportButton } from '../components/export-button/export-button';
import { SensorChartTooltip } from '../components/tooltip';
import { useExportData } from '../hooks/use-export-data';
import { useNavigateCharts } from '../hooks/use-history-charts';

type EnergyChartProps = {
  channel: IDeviceChannel;
  active?: boolean;
};

const EnergyChart: React.FC<EnergyChartProps> = ({ channel, active }) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallation } = useInstallationContext();
  const { mappedPhases, handlePhaseChange } = useMeterPhases(channel);
  const { exportEnergyData } = useExportData(channel);
  const [activeAggregatedParameter, setActiveAggregatedParameter] = useState<EnergyMeasurementParameter>(
    EnergyMeasurementParameter.ActivePower,
  );
  const { activeRange, page, currentRangeLabel, dateFromRange, onClickActiveMeasurementRange, setPage } =
    useNavigateCharts();
  const { data: chartData, isLoading } = useChartData<EnergyHistory>(
    channel,
    DeviceResponseType.MeasuredEnergy,
    activeRange,
    dateFromRange,
  );

  const options = [
    {
      label: t('exalus.energyMeasurements.types.ActivePower'),
      value: EnergyMeasurementParameter.ActivePower,
    },
    {
      label: t('exalus.energyMeasurements.types.ReactivePower'),
      value: EnergyMeasurementParameter.ReactivePower,
    },
    {
      label: t('exalus.energyMeasurements.types.Voltage'),
      value: EnergyMeasurementParameter.Voltage,
    },
    {
      label: t('exalus.energyMeasurements.types.Current'),
      value: EnergyMeasurementParameter.Current,
    },
  ];

  const data = useMemo(
    () =>
      chartData?.Data.map((x) => {
        const measurement = x.Values?.MeasurementAveragingParameters.get(activeAggregatedParameter);

        return {
          timestamp: new Date(x.Time).getTime(),
          unit: 'W',
          [t(`exalus.energyMeasurements.types.${activeAggregatedParameter}`)]: measurement?.Average,
        };
      }),
    [chartData?.Data, activeAggregatedParameter],
  );

  const handleOnChange = (value: EnergyMeasurementParameter) => setActiveAggregatedParameter(value);

  const aggregatedData = useMemo(
    () => chartData?.AggregateDataList.find((x) => x.DataEntryIdentity === activeAggregatedParameter)?.AggregateData,
    [chartData?.AggregateDataList],
  );

  return (
    <>
      <ControlWrapper className={classNames('control-wrapper--full-space no-center', { active: active })}>
        <div className="exalus-chart-container">
          <InputSelect
            options={options}
            value={activeAggregatedParameter}
            onChange={handleOnChange}
            reverse
            sx={{ mb: 4 }}
            label={''}
          />
          <TimeRanges
            activeMeasurementRange={activeRange}
            setActiveMeasurementRange={onClickActiveMeasurementRange}
            disableMonth
            disableYear
          />
          <RangeHeader prevAvailable={true} nextAvailable={page < 0} range={currentRangeLabel} setPage={setPage} />
          {data && data.length > 0 ? (
            <>
              <div className="binary-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart
                    width={500}
                    height={150}
                    data={data}
                    margin={{
                      top: 15,
                      right: 20,
                      left: 5,
                      bottom: 0,
                    }}
                  >
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tick={<CustomizedXTick />}
                      height={44}
                      scale="time"
                      allowDuplicatedCategory={!false}
                    />
                    <YAxis unit={energyParams[activeAggregatedParameter].unit} />
                    <Tooltip content={<SensorChartTooltip />} />

                    <Area
                      type="monotone"
                      dataKey={t(`exalus.energyMeasurements.types.${activeAggregatedParameter}`)}
                      stroke={selectedInstallation?.hexColor}
                      fill={selectedInstallation?.hexColor}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                {channel.GetDevice().Channels.length > 1 && (
                  <div className="energy-consumption__button-group">
                    <Phases
                      mappedPhases={mappedPhases}
                      selectedPhases={[channel.Number]}
                      onClickPhase={handlePhaseChange}
                    />
                  </div>
                )}
              </div>
              <ExportButton
                disabled={!chartData || !chartData.Data.length}
                handleExport={() => exportEnergyData(chartData)}
              />
            </>
          ) : (
            <ChartEmptyContainer isLoading={isLoading} />
          )}
        </div>
      </ControlWrapper>
      {aggregatedData && (
        <ChartSummary
          activeRange={activeRange}
          aggregateData={aggregatedData}
          unit={energyParams[activeAggregatedParameter].unit}
        />
      )}
    </>
  );
};

export default EnergyChart;
